/**
 * EditBallDialog — full-modal ball editor.
 *
 * Opens when the operator clicks EDIT BALL on the scoring page. Contents:
 *  - Ball picker (dropdown of recent balls — change ball without closing)
 *  - Tabbed editor: Outcome / Extras / Wicket / Players / Spatial /
 *                   Performance / Commentary
 *  - Save (stays open, reloads with same ball) / Save & close / Cancel
 *
 * State shape mirrors the backend PATCH payload exactly:
 *   { runs: {...}, wicket: {...}, analytics: {...}, spatial: {...},
 *     striker, non_striker, bowler, auto_commentary, ... }
 *
 * Edit semantics (PARTIAL UPDATE):
 *  - Every tab pre-populates from the loaded ball.
 *  - Only fields the operator actually changes are added to `edits`.
 *  - PATCH sends only `edits` — the backend preserves anything not in the
 *    payload (analytics, spatial, video-clip offsets, DRS, etc.).
 *
 * Why this matters:
 *  - Previous version sent flat fields like {completed_runs, is_wide,
 *    wicket_type} which the backend silently dropped (it expected
 *    {runs: {...}, wicket: {...}}). Edits never persisted.
 *  - Previous backend also wiped EVERY sub-record on every edit, destroying
 *    the per-ball video clip and analytics. Both ends are now fixed.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { X, Save, RotateCcw, ChevronDown, Film, Scissors } from "lucide-react";
import { getBallForEdit, updateBall, listInningsBalls } from "../../api/ballbyball.api";
import { getMatchMasterRecording, resliceBall } from "../../api/recordings.api";
import { getMatchScorecard } from "../../api/matches.api";
import ZoneMatrixPicker from "./ZoneMatrixPicker";
import SpatialPicker from "./SpatialPicker";
import VoiceUtterancesPanel from "./VoiceUtterancesPanel";
import { playerDisplayName } from "../../hooks/useNameMode";
import useDialogDismiss from "../../hooks/useDialogDismiss";
import { formatBallReference, deriveOverBase } from "../../utils/hundredDisplay";

const WICKET_TYPES = [
  "BOWLED", "LBW", "CAUGHT", "RUN_OUT", "STUMPED", "HIT_WICKET",
  "MANKAD", "HIT_BALL_TWICE", "OBSTRUCTING_FIELD",
  "RETIRED_HURT", "RETIRED_OUT", "TIMED_OUT",
];
// 2026-05-29 — Video Clip + Voice tabs removed per founder ask.
// 2026-06-09 (CF-P0-06) — Voice tab restored so the operator can
// review the audio archive for the ball + record an "I actually said"
// correction. VoiceUtterancesPanel handles audio playback +
// /api/scoring/voice/correction/ submission. Without this entry point
// the panel was orphaned and VoiceUtterance.operator_correction never
// populated, leaving the STT improvement pipeline blind to real
// scorer corrections.
const TABS = [
  { k: "outcome",     l: "Outcome" },
  { k: "extras",      l: "Extras" },
  { k: "wicket",      l: "Wicket" },
  { k: "players",     l: "Players" },
  { k: "spatial",     l: "Spatial" },
  { k: "release",     l: "Release" },
  { k: "performance", l: "Performance" },
  { k: "commentary",  l: "Commentary" },
  { k: "voice",       l: "Voice" },
];

// Vocabularies for the Performance Data tab.
// Grouped options render as <optgroup>s; flat lists render as a plain select.
// SelectFreeField / GroupedSelectField will both surface any legacy /
// free-form value the server returns even if it's outside the canonical
// option list, so existing rows always pre-populate cleanly.
const DELIVERY_TYPES = [
  { group: "Pace bowler", items: [
    "No Movement", "In Swinger", "Out Swinger", "Seam In", "Seam Away",
    "Reverse Inswinger", "Reverse Outswinger", "Angled In", "Angled Away",
    "Slower Ball", "Knuckle Ball", "Off Cutter", "Leg Cutter", "Back of Hand",
  ]},
  { group: "Spinner", items: [
    "No Turn", "Off Break", "Left arm Orthodox", "Arm Ball", "Carrom Ball",
    "Doosra", "Slider", "Leg Spinner", "Googly", "Drifter", "Top Spinner",
    "Flipper", "Quicker Ball",
  ]},
];
const STROKES = [
  { group: "Defence", items: [
    "No shot", "Leave", "Forward Defence", "Backward Defence", "Padded", "Fended",
  ]},
  { group: "Offence", items: [
    "Straight Drive", "Off Drive", "On Drive", "Cover Drive", "Square Drive",
    "Flick", "Slog", "Sweep", "Reverse Sweep", "Slog Sweep", "Paddle Sweep",
    "Scoop", "Reverse Scoop", "Paddle Scoop", "Switch Hit",
    "Cut", "Late Cut", "Upper Cut", "Square Cut",
    "Pull", "Hook", "Worked", "Pushed", "Steer", "Dropped", "Glance", "Guided",
  ]},
];
const FOOT_MOVEMENTS = [
  "No Movement", "Front Foot", "Back Foot", "Down the Track", "Backs Away",
  "Ducked", "Swayed Away", "Deep in Crease", "Moves Infront", "Moves Offside",
  "Moves Legside",
];
const AIR_MOVEMENTS = [
  "GROUND", "FLAT", "LOOPY", "HIGH",
];
const SHOT_CONNECTIONS = [
  "Normal", "Well Timed", "Middled", "Mis-timed", "Play and Miss",
  "Play and Miss (Legside)", "Shoulder Arms", "Left", "Bat-pad", "Padded",
  "Gloved", "Hit Pad", "Hit Body", "Hit Helmet",
  "Outside Edge", "Inside Edge", "Top Edge", "Bottom Edge", "Leading Edge",
  "Thick Edge", "Spliced",
];
const BOUNCE_CATEGORIES = [
  "EXTRA", "STANDARD", "KEPT_LOW", "VARIABLE",
];
const KEEPER_ACTIVITY = [
  "Standard", "Up-to-stumps", "Leg-side-shift", "Off-side-shift",
];
const SPIN_DIRECTIONS = [
  "OFF_SPIN", "LEG_SPIN", "TOP_SPIN", "SLIDER", "WRONG_UN", "ARM_BALL",
  "CARROM", "DOOSRA",
];
const DRIFT_DIRECTIONS = [
  "IN", "OUT", "NONE",
];
const TURN_AFTER_PITCH = [
  "Big", "Small", "Straight", "Skid",
];

// Release-tab vocabularies — see scoring/models/release.py for the
// underlying BallReleaseData fields. "Bowler release position" is what
// scorers commonly call "bowler's guard" (where the bowler delivers from
// relative to the stumps).
const BOWLER_RELEASE_POSITIONS = [
  "OVER_THE_WICKET",
  "DIAGONAL",        // between over and round — released across the body
  "ROUND_THE_WICKET",
  "WIDE_OVER",       // wide of crease, over the wicket
  "WIDE_ROUND",      // wide of crease, round the wicket
];
// 2026-08-03 — release-point anomaly (founder ask). Distinct from the
// position above: captures HOW the ball left the hand relative to the
// crease/arm norm. Live coding screen defaults this to NORMAL.
const BOWLER_RELEASE_POINTS = [
  "NORMAL",
  "WIDE_OF_CREASE",
  "BEHIND_CREASE",
  "LOW_ARM",
];
const KEEPER_POSITIONS = [
  "standard",
  "up-to-stumps",
  "leg-side-shift",
  "right-side-shift",
];
const BALL_TYPES = [
  "NEW", "REPLACEMENT", "OLD",
];
const BREAK_TYPES = [
  "DRINKS", "INNINGS_BREAK", "STRATEGIC_TIMEOUT",
  "RAIN", "INJURY", "FLOODLIGHT_FAILURE", "OTHER",
];

// Spatial — body-impact vocabularies (LBW analysis). See spatial.py.
const BATTER_IMPACT_HEIGHTS = [
  "HEAD", "SHOULDER", "CHEST", "WAIST", "THIGH", "KNEE", "SHIN", "YORK",
];
const BATTER_IMPACT_LINES = [
  "WOFF", "OFF", "MOFF", "MID", "MLEG", "LEG", "WLEG",
];

// 2026-07-07 — Fielding position where the ball ended up (as opposed
// to where the fielder was standing at delivery — that's BallFielding).
// Keys must match scoring.Ball.FIELDING_POSITION_CHOICES.
const FIELDING_POSITIONS_LANDED = [
  { v: "",                 l: "— Not recorded —" },
  { v: "SLIP_1",           l: "1st Slip" },
  { v: "SLIP_2",           l: "2nd Slip" },
  { v: "SLIP_3",           l: "3rd Slip" },
  { v: "SLIP_4",           l: "4th Slip" },
  { v: "GULLY",            l: "Gully" },
  { v: "LEG_SLIP",         l: "Leg Slip" },
  { v: "SHORT_LEG",        l: "Short Leg" },
  { v: "SILLY_POINT",      l: "Silly Point" },
  { v: "SILLY_MID_ON",     l: "Silly Mid-on" },
  { v: "SILLY_MID_OFF",    l: "Silly Mid-off" },
  { v: "LEG_GULLY",        l: "Leg Gully" },
  { v: "POINT",            l: "Point" },
  { v: "COVER",            l: "Cover" },
  { v: "EXTRA_COVER",      l: "Extra Cover" },
  { v: "MID_OFF",          l: "Mid-off" },
  { v: "MID_ON",           l: "Mid-on" },
  { v: "MID_WICKET",       l: "Mid-wicket" },
  { v: "SQUARE_LEG",       l: "Square Leg" },
  { v: "FINE_LEG",         l: "Fine Leg" },
  { v: "THIRD_MAN",        l: "Third Man" },
  { v: "DEEP_POINT",       l: "Deep Point" },
  { v: "DEEP_COVER",       l: "Deep Cover" },
  { v: "DEEP_EXTRA_COVER", l: "Deep Extra Cover" },
  { v: "LONG_OFF",         l: "Long-off" },
  { v: "LONG_ON",          l: "Long-on" },
  { v: "DEEP_MID_WICKET",  l: "Deep Mid-wicket" },
  { v: "DEEP_SQUARE_LEG",  l: "Deep Square Leg" },
  { v: "DEEP_FINE_LEG",    l: "Deep Fine Leg" },
  { v: "DEEP_THIRD_MAN",   l: "Deep Third Man" },
  { v: "WICKET_KEEPER",    l: "Wicket-keeper" },
  { v: "BOWLER",           l: "Bowler" },
  { v: "OTHER",            l: "Other" },
];

export default function EditBallDialog({
  open,
  onClose,
  inningsId,
  matchId,           // optional — enables cross-innings tab switcher
  initialBallId,
  matchType,         // optional — for Hundred-aware ball-picker labels
  players,           // {batters, bowlers, fielders} — passed from parent
  subFielders: parentSubFielders,   // optional — parent BBP already
                                    // fetched the bowling team's reserves;
                                    // pass-through means the wagon-wheel
                                    // picker shows subs immediately without
                                    // waiting for the dialog's own fetch.
  onSaved,           // optional callback after each save
}) {
  const panelRef = useRef(null);
  // 2026-06-08 — Esc / hardware-back / backdrop / focus-trap. The
  // edit dialog blocks the operator's view of the live coding screen
  // so dismissal needs to be reachable from keyboard + Android back.
  useDialogDismiss({ open, onClose, ref: panelRef });

  const [ballList, setBallList] = useState([]);
  const [selectedBallId, setSelectedBallId] = useState(initialBallId || null);
  // Cross-innings switcher state: list of all innings available + the
  // one currently selected. Defaults to the inningsId prop (parent's
  // "current" innings during live coding); operator can click another
  // innings tab to load that innings's balls. Without this, operators
  // had no way to edit innings-1 balls while live-coding innings 2.
  const [allInnings, setAllInnings] = useState([]);   // [{id, innings_number, batting_team_id, bowling_team_id, batting_team_name}]
  const [currentInningsId, setCurrentInningsId] = useState(inningsId);
  // Cached scorecard — used to derive per-innings player rosters
  // when the operator switches the innings tab. The parent's
  // `players` prop is pinned to the CURRENT (live) innings, so
  // dropdowns would show innings-2 players even when editing an
  // innings-1 ball without this. Operator's "sometimes players are
  // wrong" complaint.
  const [scorecard, setScorecard] = useState(null);
  // Squad reserves (the 5 bench players not in the bowling XI). Surfaced
  // in the CAUGHT / STUMPED / RUN_OUT pickers + the wagon-wheel fielder
  // dropdown so substitute fielders (Law 24) can be recorded post-hoc.
  // Seeded from parentSubFielders so subs render on first paint when the
  // parent had them; the dialog's own fetch below refreshes when the
  // operator switches innings (the bowling team changes).
  const [subFielders, setSubFielders] = useState(parentSubFielders || []);
  // Keep in sync if the parent's list changes (mid-match substitution,
  // squad add, etc.).
  useEffect(() => {
    if (parentSubFielders && parentSubFielders.length > 0) {
      setSubFielders(parentSubFielders);
    }
  }, [parentSubFielders]);
  const [ball, setBall] = useState(null);
  // edits is nested to match the backend PATCH shape — see file-header docs.
  const [edits, setEdits] = useState({});
  const [tab, setTab] = useState("outcome");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  // Follow the parent's initialBallId when the operator picks a different
  // ball without closing the dialog.
  useEffect(() => {
    if (initialBallId && initialBallId !== selectedBallId) {
      setSelectedBallId(initialBallId);
    }
  }, [initialBallId]);

  // Keep currentInningsId synced with the parent's inningsId when the
  // dialog re-opens against a different innings (e.g. operator
  // navigates from QC of innings 1 to QC of innings 2).
  useEffect(() => {
    if (inningsId && inningsId !== currentInningsId) {
      setCurrentInningsId(inningsId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inningsId]);

  // If matchId was provided, load the full scorecard once so we can
  // (a) render the innings tab switcher, (b) derive per-innings
  // player rosters for the dropdowns when the operator switches
  // innings.
  useEffect(() => {
    if (!open || !matchId) {
      setAllInnings([]);
      setScorecard(null);
      return;
    }
    let alive = true;
    getMatchScorecard(matchId)
      .then((res) => {
        if (!alive) return;
        const data = res?.data || null;
        setScorecard(data);
        const list = (data?.innings || []).map((inn) => ({
          id: inn.id,
          innings_number: inn.innings_number,
          batting_team_id: inn.batting_team_id || inn.team_id || null,
          bowling_team_id: inn.bowling_team_id || null,
          batting_team_name: inn.batting_team_name || inn.team_name || `Innings ${inn.innings_number}`,
        }));
        setAllInnings(list);
      })
      .catch(() => {
        setAllInnings([]);
        setScorecard(null);
      });
    return () => { alive = false; };
  }, [open, matchId]);

  // Load the list of balls in the CURRENT innings (for the picker).
  // Re-runs when the operator switches the innings tab.
  useEffect(() => {
    if (!open || !currentInningsId) return;
    let alive = true;
    listInningsBalls(currentInningsId)
      .then((res) => {
        if (!alive) return;
        const all = res.data?.results || res.data || [];
        // "0.1 = first ball" — but which over_number that is depends on
        // the match: ingested matches number overs from 0, live-scored
        // ones from 1. Read it off the innings rather than assuming.
        const overBase = deriveOverBase(all);
        const rows = all.map((b) => ({
          id: b.id,
          label: `${formatBallReference(matchType, { overNumber: b.over_number, ballInOver: b.ball_in_over, overBase })}${b.is_legal_delivery === false ? "*" : ""}  ${b.runs_off_bat || 0}r${b.wicket_type ? " · W" : ""}`,
        }));
        setBallList(rows);
        // Auto-select the last ball ONLY if the operator hasn't
        // picked one yet on this innings. Switching innings tabs
        // clears the selection (handled below) so we land on the
        // last ball of the newly-picked innings.
        if (!selectedBallId && rows.length) setSelectedBallId(rows[rows.length - 1].id);
      })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, [open, currentInningsId]);

  // Per-innings player roster derived from the scorecard. When the
  // operator switches the innings tab, this re-computes so the
  // striker / non-striker / bowler / fielder dropdowns show the
  // CORRECT team's XI for the innings being edited. Without this,
  // editing an innings-1 ball while live-coding innings 2 showed
  // innings-2's batting side as candidates for innings-1 striker —
  // the "sometimes players are wrong" bug.
  const innPlayers = useMemo(() => {
    if (!scorecard || !currentInningsId) return null;
    const inn = (scorecard.innings || []).find((i) => i.id === currentInningsId);
    if (!inn) return null;
    const xi = scorecard.playing_xi || {};
    const battingTeam = String(inn.batting_team_id || "");
    const bowlingTeam = String(inn.bowling_team_id || "");
    const mapRow = (p) => ({
      id: p.player_id || p.id,
      name: p.player_name || p.name || "",
      nick_name: p.nick_name || null,
      first_name: p.first_name || "",
      last_name: p.last_name || "",
    });
    const batters  = (xi[battingTeam] || []).map(mapRow);
    const bowlers  = (xi[bowlingTeam] || []).map(mapRow);
    // Fielders = the bowling team's XI (cricket convention: the
    // fielding side is the bowling side). Caught_by / run_out
    // fielder dropdowns use this.
    const fielders = bowlers;
    return { batters, bowlers, fielders, bowlingTeamId: bowlingTeam };
  }, [scorecard, currentInningsId]);

  // Effective players prop: prefer the per-innings derived list when
  // we have scorecard data, fall back to whatever the parent passed
  // (covers the cases where matchId wasn't passed in).
  const effectivePlayers = innPlayers || players;

  // Squad reserves for the bowling team — the 5 bench players not in the
  // XI. Surfaced via optgroup in the wicket pickers + wagon-wheel fielder
  // dropdown so substitute fielders can be picked post-hoc on edit.
  useEffect(() => {
    if (!open) return;
    const matchId = ball?.match_id;
    const bowlingTeamId = innPlayers?.bowlingTeamId;
    if (!matchId || !bowlingTeamId) return;
    let alive = true;
    (async () => {
      try {
        const api = (await import("../../api/axios")).default;
        const res = await api.get(`/api/matches/${matchId}/squad/`);
        if (!alive) return;
        const t1 = res.data?.team1;
        const t2 = res.data?.team2;
        const team =
          String(t1?.team_id) === String(bowlingTeamId) ? t1
          : String(t2?.team_id) === String(bowlingTeamId) ? t2
          : null;
        setSubFielders(team?.reserves || []);
      } catch {
        if (alive) setSubFielders([]);
      }
    })();
    return () => { alive = false; };
  }, [open, ball?.match_id, innPlayers?.bowlingTeamId]);

  // Load the picked ball
  useEffect(() => {
    if (!open || !selectedBallId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    getBallForEdit(selectedBallId)
      .then((res) => {
        if (!alive) return;
        setBall(res.data);
        setEdits({});
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || "Failed to load ball");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, selectedBallId]);

  // IMPORTANT: every hook must run on every render — keep them all ABOVE the
  // open-gate early return. Putting useMemo (or any hook) after an early
  // return changes the hook count between renders and triggers React #310.
  // "release" added 2026-05-19 so its sub-edits count as changes and persist
  // via the backend `release` PATCH key (see ball_edit_service.py).
  const NESTED_GROUPS = ["runs", "wicket", "analytics", "spatial", "release"];

  // T216 / P0-4: confirm before discarding unsaved edits when the
  // operator picks a different ball. Returns true if it's safe to
  // proceed (no edits, or user confirmed discard). false means
  // "stay on the current ball; user cancelled". Built as a callback
  // because both the BallPicker dropdown and the Innings switcher
  // need to use it.
  const confirmDiscardIfDirty = () => {
    // Read edits.length-like signal via the same NESTED_GROUPS logic
    // hasChanges uses below. Inline because hasChanges memo is declared
    // a few lines down and we want the function to be safe to call
    // from any handler without ordering anxiety.
    const top = Object.keys(edits).filter((k) => !NESTED_GROUPS.includes(k));
    const dirty = top.length > 0
      || NESTED_GROUPS.some((g) => edits[g] && Object.keys(edits[g]).length > 0);
    if (!dirty) return true;
    // eslint-disable-next-line no-alert
    return window.confirm(
      "You have unsaved edits on this ball. Discard them and switch?"
    );
  };

  const hasChanges = useMemo(() => {
    const top = Object.keys(edits).filter((k) => !NESTED_GROUPS.includes(k));
    if (top.length) return true;
    return NESTED_GROUPS.some(
      (g) => edits[g] && Object.keys(edits[g]).length > 0
    );
  }, [edits]);

  const changeCount = useMemo(() => {
    let n = Object.keys(edits).filter((k) => !NESTED_GROUPS.includes(k)).length;
    NESTED_GROUPS.forEach((g) => {
      if (edits[g]) n += Object.keys(edits[g]).length;
    });
    return n;
  }, [edits]);

  if (!open) return null;

  // Merge ball + edits into a unified view object. The runs/wicket/analytics/
  // spatial/release sub-objects deep-merge so a per-field edit still sees
  // prior state. The server pre-populates `release` via serialize_ball_for_edit
  // when a BallReleaseData row exists.
  const merged = ball ? {
    ...ball,
    ...edits,
    runs:      { ...(ball.runs || {}),      ...(edits.runs      || {}) },
    wicket:    { ...(ball.wicket || {}),    ...(edits.wicket    || {}) },
    analytics: { ...(ball.analytics || {}), ...(edits.analytics || {}) },
    spatial:   { ...(ball.spatial || {}),   ...(edits.spatial   || {}) },
    release:   { ...(ball.release || {}),   ...(edits.release   || {}) },
  } : null;

  // Completeness percentage — a lightweight signal so QC operators
  // know at a glance how much of the ball's coding has been filled in.
  // Counts a fixed set of high-signal fields (strikers/bowler, basic
  // analytics, basic spatial, commentary) so the score is comparable
  // across balls regardless of which optional fields exist. NOT
  // load-bearing — purely visual.
  const completeness = (() => {
    if (!merged) return null;
    const sp = merged.spatial || {};
    const an = merged.analytics || {};
    const checks = [
      !!merged.striker,
      !!merged.non_striker,
      !!merged.bowler,
      !!an.stroke,
      !!an.shot_connection,
      !!an.foot_movement,
      !!(sp.structured_position || sp.wagon_wheel_x != null),
      !!(sp.pitch_zone || sp.stump_zone),
      !!(merged.commentary || merged.auto_commentary),
    ];
    const done = checks.filter(Boolean).length;
    return { done, total: checks.length, pct: Math.round((done / checks.length) * 100) };
  })();

  // Wicket sub-field validation — every wicket type has a known set of
  // required IDs (caught_by_id for CAUGHT, stumped_by_id for STUMPED,
  // etc.). Today these are conditionally rendered but never required;
  // operators routinely save CAUGHT dismissals without picking the
  // catching fielder, and QC has to re-edit hours later. Compute the
  // missing fields here so we can surface them inline + block Save.
  const wicketIssues = (() => {
    if (!merged?.wicket?.wicket_type) return [];
    const w = merged.wicket;
    const issues = [];
    if (!w.dismissed_player_id) issues.push("Pick the dismissed batter");
    const wt = w.wicket_type;
    const needsBowler = ["BOWLED", "LBW", "CAUGHT", "STUMPED", "HIT_WICKET"].includes(wt);
    if (needsBowler && !w.dismissed_by_id) issues.push("Pick the bowler who dismissed the batter");
    if (wt === "CAUGHT"  && !w.caught_by_id)        issues.push("Pick the catching fielder");
    if (wt === "STUMPED" && !w.stumped_by_id)       issues.push("Pick the stumping keeper");
    if ((wt === "RUN_OUT" || wt === "MANKAD") && !w.run_out_fielder_1_id) {
      issues.push("Pick at least the first run-out fielder");
    }
    return issues;
  })();
  const canSaveBall = hasChanges && wicketIssues.length === 0;

  // setField("auto_commentary", val) — top-level
  // setField("runs", "is_boundary", val) — nested group
  const setField = (...args) => {
    if (args.length === 2) {
      const [k, v] = args;
      setEdits((s) => ({ ...s, [k]: v }));
    } else {
      const [group, k, v] = args;
      setEdits((s) => ({ ...s, [group]: { ...(s[group] || {}), [k]: v } }));
    }
  };
  const setRuns      = (k, v) => setField("runs", k, v);
  const setWicket    = (k, v) => setField("wicket", k, v);
  const setAnalytics = (k, v) => setField("analytics", k, v);
  const setRelease   = (k, v) => setField("release", k, v);
  const setSpatial   = (k, v) => setField("spatial", k, v);

  const handleSave = async (closeAfter = false) => {
    if (!selectedBallId || !hasChanges) return;
    setSaving(true);
    setError(null);
    try {
      // Build the PATCH payload from edits. If the wicket sub-edit cleared
      // wicket_type, signal "remove wicket" by sending explicit null.
      const payload = { ...edits };
      if (payload.wicket && payload.wicket.wicket_type === "") {
        payload.wicket = null;
      }
      await updateBall(selectedBallId, payload);
      setSavedAt(new Date());
      onSaved && onSaved(selectedBallId);
      // Re-load fresh state from server so the user sees the new values
      // before making the next edit.
      const res = await getBallForEdit(selectedBallId);
      setBall(res.data);
      setEdits({});
      // 2026-06-04 — rebuild_innings_state on the backend propagates
      // striker / strike rotation through every subsequent ball. Without
      // refetching the picker rows here, the operator could navigate to
      // ball 5.4 right after editing 5.3 and see the OLD label values
      // (server data was rebuilt; this list was stale). Refetching keeps
      // the BallPicker labels in sync with the propagated rebuild.
      if (currentInningsId) {
        try {
          const ballsRes = await listInningsBalls(currentInningsId);
          const all = ballsRes.data?.results || ballsRes.data || [];
          const overBase = deriveOverBase(all);
          const rows = all.map((b) => ({
            id: b.id,
            label: `${formatBallReference(matchType, { overNumber: b.over_number, ballInOver: b.ball_in_over, overBase })}${b.is_legal_delivery === false ? "*" : ""}  ${b.runs_off_bat || 0}r${b.wicket_type ? " · W" : ""}`,
          }));
          setBallList(rows);
        } catch { /* non-fatal — keep dialog responsive */ }
      }
      if (closeAfter) onClose();
    } catch (e) {
      const errData = e?.response?.data;
      setError(
        errData?.detail
        || (errData ? JSON.stringify(errData) : null)
        || e?.message
        || "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setEdits({});

  const hasClip = ball?.video_start_ms != null && ball?.video_end_ms != null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] p-2 sm:p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Edit ball"
        className="bg-bg-primary rounded-xl sm:rounded-2xl w-full max-w-sm sm:max-w-md md:max-w-3xl max-h-[95vh] sm:max-h-[90vh] border border-bg-secondary shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-3 sm:px-5 py-2 sm:py-3 border-b border-border-default">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-wrap">
            <h3 className="text-base font-semibold text-text-primary shrink-0">Edit ball</h3>
            {completeness && (
              <span
                className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-mono shrink-0 ${
                  completeness.pct >= 80
                    ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                    : completeness.pct >= 50
                      ? "bg-amber-500/15 text-amber-300 border border-amber-500/40"
                      : "bg-rose-500/15 text-rose-300 border border-rose-500/40"
                }`}
                title={`${completeness.done} of ${completeness.total} signal fields filled (strikers, bowler, analytics, spatial, commentary)`}
              >
                {completeness.pct}% complete
              </span>
            )}
            {/* Innings switcher — only shown when matchId is provided and
                multiple innings exist. Lets operators edit innings-1
                balls while live-coding innings-2 (and vice versa)
                without leaving the dialog. */}
            {allInnings.length > 1 && (
              <div className="flex items-center gap-1">
                {allInnings.map((inn) => {
                  const active = inn.id === currentInningsId;
                  return (
                    <button
                      key={inn.id}
                      onClick={() => {
                        if (inn.id === currentInningsId) return;
                        if (!confirmDiscardIfDirty()) return;
                        setCurrentInningsId(inn.id);
                        setSelectedBallId(null);   // auto-pick last ball of the new innings via the load effect
                        setBall(null);
                        setEdits({});
                      }}
                      title={inn.batting_team_name}
                      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
                        active
                          ? "bg-button-primary/20 text-button-primary border border-button-primary/40"
                          : "bg-bg-secondary text-text-secondary hover:text-text-primary border border-border-default"
                      }`}
                    >
                      Inns {inn.innings_number}
                    </button>
                  );
                })}
              </div>
            )}
            <BallPicker
              rows={ballList}
              selected={selectedBallId}
              onChange={(nextId) => {
                // T216 / P0-4: guard against silent data loss when
                // operator switches ball with unsaved edits. The load
                // effect on selectedBallId change calls setEdits({})
                // unconditionally; without this check, picker dropdowns
                // were a one-way trip to lost work.
                if (nextId === selectedBallId) return;
                if (!confirmDiscardIfDirty()) return;
                setSelectedBallId(nextId);
              }}
            />
            {hasClip && (
              <span className="text-[10px] uppercase tracking-wider text-emerald-400 flex items-center gap-1"
                title={`Video clip @ ${ball.video_start_ms}–${ball.video_end_ms} ms — preserved on save`}>
                <Film className="w-3 h-3" /> clip
              </span>
            )}
            {savedAt && (
              <span className="text-[10px] uppercase tracking-wider text-emerald-400">
                Saved {savedAt.toLocaleTimeString()}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs — scroll horizontally on phones (cleaner than 3-line
            wrap), wrap on tablet+ for layouts that prefer it. */}
        <div className="flex gap-1 px-3 sm:px-5 pt-3 border-b border-border-default overflow-x-auto sm:flex-wrap sm:overflow-visible whitespace-nowrap">
          {TABS.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-3 py-2 sm:py-1.5 text-xs uppercase tracking-wider rounded-t-md shrink-0 ${
                tab === t.k
                  ? "bg-bg-secondary text-text-primary border-x border-t border-border-default"
                  : "text-text-secondary hover:text-text-primary"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-5">
          {loading && <div className="text-text-secondary text-sm">Loading ball…</div>}
          {!loading && !ball && !error && <div className="text-text-secondary text-sm">Pick a ball above.</div>}
          {error && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded p-3 text-sm text-rose-300 mb-3 whitespace-pre-wrap break-words">{error}</div>
          )}

          {!loading && ball && tab === "outcome" && (
            <Section>
              <NumberField
                label="Runs off bat"
                value={merged.runs.runs_off_bat ?? 0}
                onChange={(v) => setRuns("runs_off_bat", v ?? 0)}
              />
              <NumberField
                label="Completed runs (running)"
                value={merged.runs.completed_runs ?? 0}
                onChange={(v) => setRuns("completed_runs", v ?? 0)}
              />
              <Toggle label="Boundary (4 / 6)" value={!!merged.runs.is_boundary}
                onChange={(v) => setRuns("is_boundary", v)} />
              <Toggle label="Short run" value={!!merged.runs.is_short_run}
                onChange={(v) => setRuns("is_short_run", v)} />
              <Toggle label="Quick running" value={!!merged.runs.is_quick_running}
                onChange={(v) => setRuns("is_quick_running", v)} />
              <Toggle label="Free hit" value={!!merged.runs.is_free_hit}
                onChange={(v) => setRuns("is_free_hit", v)} />
              {/* 2026-07-07 — Opta/vendor gap-review batch. Two picks
                  filled the raw-data gap Ball.batter_guard +
                  Ball.fielding_position_landed. Optional — blank
                  means the scorer skipped. */}
              <SelectField
                label="Batter guard"
                value={merged.batter_guard || ""}
                onChange={(v) => setField("batter_guard", v)}
                options={[
                  { v: "",        l: "— Not recorded —" },
                  { v: "LEG",     l: "Leg stump" },
                  { v: "MIDDLE",  l: "Middle stump" },
                  { v: "TWO_LEG", l: "Two-leg (middle & leg)" },
                  { v: "OFF",     l: "Off stump" },
                  { v: "CENTER",  l: "Centre" },
                ]}
              />
              <SelectField
                label="Ball ended up at"
                value={merged.fielding_position_landed || ""}
                onChange={(v) => setField("fielding_position_landed", v)}
                options={FIELDING_POSITIONS_LANDED}
              />
            </Section>
          )}

          {!loading && ball && tab === "extras" && (
            <Section>
              <ExtrasPicker
                runs={merged.runs || {}}
                onChange={(patch) =>
                  setEdits((s) => ({ ...s, runs: { ...(s.runs || {}), ...patch } }))
                }
              />
              <Toggle label="Dead ball" value={!!merged.is_dead_ball}
                onChange={(v) => setField("is_dead_ball", v)} />
              {((merged.runs?.no_balls || 0) > 0) && (
                <SelectField
                  label="No-ball type"
                  value={merged.runs.no_ball_type || ""}
                  onChange={(v) => setRuns("no_ball_type", v)}
                  options={[
                    { v: "",                                  l: "—" },
                    { v: "front_foot",                        l: "Front Foot" },
                    { v: "back_foot",                         l: "Back Foot" },
                    { v: "bouncer",                           l: "Bouncer" },
                    { v: "beamer",                            l: "Beamer" },
                    { v: "breaking_wicket",                   l: "Breaking Wicket" },
                    { v: "fielders_behind_square",            l: "Fielders behind square" },
                    { v: "fielding_restriction_violation",    l: "Fielding Restriction Violation" },
                    { v: "double_pitching_cut_strip_missing", l: "Double Pitching / Cut Strip Missing" },
                    { v: "wicket_keeper_error",               l: "Wicket Keeper Error" },
                    { v: "other",                             l: "Other" },
                  ]}
                />
              )}
              {/* 2026-06-10 Batch 4 — Law 22 wide subtype. Backend field
                  Ball.wide_type shipped with migration 0050. ICC
                  scorecards split "Wides (off-side)" vs "Wides (leg-side)"
                  subtotals; without the subtype the breakdown was being
                  lost on every wide delivery. Only shown when the
                  extras have a wide count, mirroring the no_ball_type
                  picker pattern above. */}
              {((merged.runs?.wides || 0) > 0) && (
                <SelectField
                  label="Wide type"
                  value={merged.runs.wide_type || ""}
                  onChange={(v) => setRuns("wide_type", v)}
                  options={[
                    { v: "",                l: "—" },
                    { v: "OFFSIDE",         l: "Off side" },
                    { v: "LEGSIDE",         l: "Leg side" },
                    { v: "ABOVE_SHOULDER",  l: "Above shoulder" },
                    { v: "OTHER",           l: "Other" },
                  ]}
                />
              )}
              {merged.is_dead_ball && (
                <TextField
                  label="Dead-ball reason"
                  value={merged.dead_ball_reason || ""}
                  onChange={(v) => setField("dead_ball_reason", v)}
                  placeholder="e.g. ball lodged in pads / fielder injured"
                />
              )}
            </Section>
          )}

          {!loading && ball && tab === "wicket" && (
            <Section>
              {wicketIssues.length > 0 && (
                <div className="col-span-full rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200">
                  <div className="font-semibold mb-1">Wicket data incomplete — Save is blocked until:</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {wicketIssues.map((m) => <li key={m}>{m}</li>)}
                  </ul>
                </div>
              )}
              <SelectField
                label="Wicket type"
                value={merged.wicket.wicket_type || ""}
                onChange={(v) => setWicket("wicket_type", v)}
                options={[{ v: "", l: "— No wicket —" }, ...WICKET_TYPES.map((w) => ({ v: w, l: w }))]}
              />
              {merged.wicket.wicket_type && (
                <>
                  <PlayerField
                    label="Dismissed player"
                    value={merged.wicket.dismissed_player_id}
                    onChange={(v) => setWicket("dismissed_player_id", v)}
                    players={effectivePlayers?.batters || []}
                  />
                  <PlayerField
                    label="Dismissed by (bowler)"
                    value={merged.wicket.dismissed_by_id}
                    onChange={(v) => setWicket("dismissed_by_id", v)}
                    players={effectivePlayers?.bowlers || []}
                  />
                  {merged.wicket.wicket_type === "CAUGHT" && (
                    <PlayerField
                      label="Caught by (fielder)"
                      value={merged.wicket.caught_by_id}
                      onChange={(v) => setWicket("caught_by_id", v)}
                      players={effectivePlayers?.fielders || []}
                      subPlayers={subFielders}
                    />
                  )}
                  {merged.wicket.wicket_type === "STUMPED" && (
                    <PlayerField
                      label="Stumped by (keeper)"
                      value={merged.wicket.stumped_by_id}
                      onChange={(v) => setWicket("stumped_by_id", v)}
                      players={effectivePlayers?.fielders || []}
                      subPlayers={subFielders}
                    />
                  )}
                  {(merged.wicket.wicket_type === "RUN_OUT" || merged.wicket.wicket_type === "MANKAD") && (
                    <>
                      <PlayerField
                        label="Run-out fielder 1"
                        value={merged.wicket.run_out_fielder_1_id}
                        onChange={(v) => setWicket("run_out_fielder_1_id", v)}
                        players={effectivePlayers?.fielders || []}
                        subPlayers={subFielders}
                      />
                      <PlayerField
                        label="Run-out fielder 2"
                        value={merged.wicket.run_out_fielder_2_id}
                        onChange={(v) => setWicket("run_out_fielder_2_id", v)}
                        players={effectivePlayers?.fielders || []}
                        subPlayers={subFielders}
                      />
                      <SelectField
                        label="Run-out subtype"
                        value={merged.run_out_subtype || ""}
                        onChange={(v) => setField("run_out_subtype", v)}
                        options={[
                          { v: "",                 l: "— Standard —" },
                          { v: "MANKAD",           l: "Mankad (non-striker, ball in delivery stride)" },
                          { v: "NON_STRIKER_END",  l: "Non-striker's end (mid-pitch)" },
                          { v: "STRIKER_END",      l: "Striker's end (failed run)" },
                          { v: "BOTH_BATTERS_OUT_OF_CREASE", l: "Both batters out of crease" },
                          { v: "INTERFERENCE",     l: "Batter interference / obstruction" },
                        ]}
                      />
                    </>
                  )}
                  {(merged.wicket.wicket_type === "LBW"
                    || merged.wicket.wicket_type === "CAUGHT"
                    || merged.wicket.wicket_type === "STUMPED") && (
                    <SelectField
                      label="Appealed for"
                      value={merged.appealed_for || ""}
                      onChange={(v) => setField("appealed_for", v)}
                      options={[
                        { v: "",         l: "— Not recorded —" },
                        { v: "LBW",      l: "LBW" },
                        { v: "CAUGHT",   l: "Caught" },
                        { v: "STUMPED",  l: "Stumped" },
                        { v: "RUN_OUT",  l: "Run-out" },
                        { v: "OTHER",    l: "Other" },
                      ]}
                    />
                  )}
                  {(merged.wicket.wicket_type === "CAUGHT"
                    || merged.wicket.wicket_type === "RUN_OUT"
                    || merged.wicket.wicket_type === "STUMPED"
                    || merged.wicket.wicket_type === "MANKAD") && (
                    // Operator declares the full post-ball state. The
                    // backend derives batters_crossed from this so the
                    // existing engine logic keeps working.
                    <SelectField
                      label="Next Ball Strike Outcome"
                      value={merged.next_ball_strike_outcome || ""}
                      onChange={(v) => setField("next_ball_strike_outcome", v)}
                      options={[
                        { v: "",                  l: "— Not recorded —" },
                        { v: "NEW_STRIKER",       l: "New Batter at Striker's End" },
                        { v: "NEW_NON_STRIKER",   l: "New Batter at Non-Striker's End" },
                        { v: "EXIST_STRIKER",     l: "Existing Batter at Striker's End" },
                        { v: "EXIST_NON_STRIKER", l: "Existing Batter at Non-Striker's End" },
                        { v: "OVER_NEW",          l: "Over Complete — New Batter Starts Next Over" },
                        { v: "OVER_EXIST",        l: "Over Complete — Existing Batter Starts Next Over" },
                      ]}
                    />
                  )}
                </>
              )}
            </Section>
          )}

          {!loading && ball && tab === "players" && (
            <Section>
              <PlayerField label="Striker"     value={merged.striker}     onChange={(v) => setField("striker", v)}     players={effectivePlayers?.batters || []} />
              <PlayerField label="Non-striker" value={merged.non_striker} onChange={(v) => setField("non_striker", v)} players={effectivePlayers?.batters || []} />
              <PlayerField label="Bowler"      value={merged.bowler}      onChange={(v) => setField("bowler", v)}      players={effectivePlayers?.bowlers || []} />
              <SelectField label="Striker hand" value={merged.striker_hand || "RIGHT"} onChange={(v) => setField("striker_hand", v)}
                options={[{ v: "RIGHT", l: "Right" }, { v: "LEFT", l: "Left" }]} />
              <SelectField label="Bowler hand" value={merged.bowler_hand || "RIGHT"} onChange={(v) => setField("bowler_hand", v)}
                options={[{ v: "RIGHT", l: "Right" }, { v: "LEFT", l: "Left" }]} />
            </Section>
          )}

          {!loading && ball && tab === "spatial" && (
            <SpatialEditor
              // Key by ball id so the internal subTab / structuredRegion
              // state remounts fresh on every ball change AND on every
              // save (which bumps the ball reference via getBallForEdit).
              // Without the remount, the wagon wheel could keep an
              // earlier ball's structuredRegion highlighted after the
              // operator switched balls, and pitch-zone edits made on
              // one ball would visually persist their tab focus into
              // the next — the "line/length doesn't update on the wagon
              // wheel" report.
              key={ball?.id || selectedBallId}
              merged={merged}
              setEdits={setEdits}
              players={effectivePlayers}
              setSpatial={setSpatial}
              subFielders={subFielders}
            />
          )}

          {!loading && ball && tab === "release" && (
            <Section>
              <SelectFreeField
                label="Bowler release position (over / round / wide)"
                value={merged.release.bowler_release_position || ""}
                onChange={(v) => setRelease("bowler_release_position", v)}
                options={BOWLER_RELEASE_POSITIONS}
              />
              <SelectFreeField
                label="Bowler release point"
                value={merged.release.bowler_release_point || ""}
                onChange={(v) => setRelease("bowler_release_point", v)}
                options={BOWLER_RELEASE_POINTS}
              />
              {/* Wicket-keeper position lives on the Performance tab
                  (analytics.keeper_activity) — that's the field the live
                  coding screen's "Keeper" chip group writes to. The old
                  Release-tab dropdown wrote to a separate
                  release.wicket_keeper_position that nothing else read,
                  so it permanently displayed whatever stale default ("WK
                  Up" / "Diagonal") was first stamped on the row and
                  ignored what the operator had actually coded. Removed
                  to eliminate the duplicate field entirely. */}
              <NumberField
                label="Speed (km/h)"
                value={merged.release.speed_kph ?? null}
                onChange={(v) => setRelease("speed_kph", v)}
              />
              <SelectFreeField
                label="Ball type"
                value={merged.release.ball_type || ""}
                onChange={(v) => setRelease("ball_type", v)}
                options={BALL_TYPES}
              />
              <Toggle
                label="Break follows this ball"
                value={!!merged.release.is_break}
                onChange={(v) => setRelease("is_break", v)}
              />
              {merged.release.is_break && (
                <SelectFreeField
                  label="Break type"
                  value={merged.release.break_type || ""}
                  onChange={(v) => setRelease("break_type", v)}
                  options={BREAK_TYPES}
                />
              )}
            </Section>
          )}

          {!loading && ball && tab === "performance" && (
            <Section>
              <GroupedSelectField
                label="Delivery type"
                value={merged.analytics.delivery_type || ""}
                onChange={(v) => setAnalytics("delivery_type", v)}
                groups={DELIVERY_TYPES}
              />
              <GroupedSelectField
                label="Stroke"
                value={merged.analytics.stroke || ""}
                onChange={(v) => setAnalytics("stroke", v)}
                groups={STROKES}
              />
              <SelectFreeField
                label="Foot movement"
                value={merged.analytics.foot_movement || ""}
                onChange={(v) => setAnalytics("foot_movement", v)}
                options={FOOT_MOVEMENTS}
              />
              <SelectFreeField
                label="Air movement"
                value={merged.analytics.air_movement || ""}
                onChange={(v) => setAnalytics("air_movement", v)}
                options={AIR_MOVEMENTS}
              />
              <SelectFreeField
                label="Shot connection"
                value={merged.analytics.shot_connection || ""}
                onChange={(v) => setAnalytics("shot_connection", v)}
                options={SHOT_CONNECTIONS}
              />
              <SelectFreeField
                label="Bounce"
                value={merged.analytics.bounce_category || ""}
                onChange={(v) => setAnalytics("bounce_category", v)}
                options={BOUNCE_CATEGORIES}
              />
              <SelectFreeField
                label="Keeper"
                value={merged.analytics.keeper_activity || ""}
                onChange={(v) => setAnalytics("keeper_activity", v)}
                options={KEEPER_ACTIVITY}
              />
              <SelectFreeField
                label="Spin direction"
                value={merged.analytics.spin_direction || ""}
                onChange={(v) => setAnalytics("spin_direction", v)}
                options={SPIN_DIRECTIONS}
              />
              <SelectFreeField
                label="Drift"
                value={merged.analytics.drift_direction || ""}
                onChange={(v) => setAnalytics("drift_direction", v)}
                options={DRIFT_DIRECTIONS}
              />
              <SelectFreeField
                label="Turn"
                value={merged.analytics.turn_after_pitch || ""}
                onChange={(v) => setAnalytics("turn_after_pitch", v)}
                options={TURN_AFTER_PITCH}
              />
            </Section>
          )}

          {!loading && ball && tab === "commentary" && (
            <CommentaryEditor
              ballId={selectedBallId}
              currentText={ball.auto_commentary || ""}
              onAfterRegen={(newText) => {
                // Update the local ball so the read-only view reflects
                // the regen immediately + tell the parent so the live
                // commentary feed refreshes.
                setBall((b) => b ? { ...b, auto_commentary: newText } : b);
                onSaved && onSaved(selectedBallId);
              }}
            />
          )}

          {!loading && ball && tab === "video" && (
            <VideoClipEditor
              ball={ball}
              onSavedClip={(newStart, newEnd) => {
                // Refresh the loaded ball so the header's "clip" badge
                // reflects the new offsets immediately — no need to
                // round-trip getBallForEdit because the reslice endpoint
                // already persisted them and we have the values in hand.
                setBall((b) => b ? { ...b, video_start_ms: newStart, video_end_ms: newEnd } : b);
                setSavedAt(new Date());
                onSaved && onSaved(selectedBallId);
              }}
            />
          )}

          {!loading && ball && tab === "voice" && (
            <VoiceUtterancesPanel ballId={ball.id} />
          )}
        </div>

        {/* Footer — stacks on phone so the three save buttons stay
            comfortably tappable; inline on sm+. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 sm:px-5 py-2 sm:py-3 border-t border-border-default">
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            {hasChanges ? (
              <span className="text-amber-400">{changeCount} unsaved change(s)</span>
            ) : (
              <span>No pending changes</span>
            )}
            {wicketIssues.length > 0 && (
              <span
                className="text-rose-300 cursor-pointer underline decoration-dotted"
                onClick={() => setTab("wicket")}
                title={wicketIssues.join("\n")}
              >
                · Wicket needs {wicketIssues.length} fix{wicketIssues.length === 1 ? "" : "es"}
              </span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap sm:flex-nowrap">
            {hasChanges && (
              <button onClick={handleReset} className="text-xs uppercase tracking-wider px-3 py-2.5 sm:py-1.5 rounded bg-bg-secondary text-text-secondary hover:text-text-primary flex items-center gap-1">
                <RotateCcw className="w-3.5 h-3.5" /> Reset
              </button>
            )}
            <button
              onClick={() => handleSave(false)}
              disabled={saving || !canSaveBall}
              title={wicketIssues.length > 0 ? "Fix wicket data first — see Wicket tab" : ""}
              className="text-xs uppercase tracking-wider px-4 py-2.5 sm:py-1.5 rounded bg-button-primary text-white disabled:opacity-40 flex items-center gap-1.5"
            >
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save · stay"}
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving || (hasChanges && wicketIssues.length > 0)}
              title={wicketIssues.length > 0 ? "Fix wicket data first — see Wicket tab" : ""}
              className="text-xs uppercase tracking-wider px-4 py-2.5 sm:py-1.5 rounded bg-emerald-600 text-white disabled:opacity-40 flex items-center gap-1.5"
            >
              {saving ? "Saving…" : "Save & close"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function Section({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

function Toggle({ label, value, onChange }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={`text-left px-3 py-2 rounded border ${
        value
          ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
          : "bg-bg-secondary border-border-default text-text-secondary hover:text-text-primary"
      }`}
    >
      <span className="text-xs uppercase tracking-wider mr-2">{value ? "✓" : "○"}</span>
      {label}
    </button>
  );
}

function NumberField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">{label}</span>
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        className="w-full bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm font-mono"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">{label}</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

// Grouped select with <optgroup>s (e.g. Pace bowler / Spinner for delivery
// type). Surfaces the stored value as an "Other" option if it's outside
// the canonical groups so legacy / free-form rows still pre-populate.
function GroupedSelectField({ label, value, onChange, groups }) {
  const inAnyGroup = useMemo(
    () => groups.some((g) => g.items.includes(value)),
    [groups, value],
  );
  return (
    <label className="block">
      {/* T142: selective field undo — small × next to the label clears
          this single field. Faster than opening the dropdown and
          picking "— not set —". Only shown when a value is set. */}
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-text-secondary mb-1">
        <span>{label}</span>
        {value && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); onChange(""); }}
            className="text-rose-300 hover:text-rose-100 text-[10px] leading-none px-1 rounded hover:bg-rose-500/20"
            title={`Clear ${label}`}
          >×</button>
        )}
      </span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm"
      >
        <option value="">— not set —</option>
        {groups.map((g) => (
          <optgroup key={g.group} label={g.group}>
            {g.items.map((o) => <option key={o} value={o}>{o}</option>)}
          </optgroup>
        ))}
        {value && !inAnyGroup && (
          <optgroup label="Other (legacy)">
            <option value={value}>{value}</option>
          </optgroup>
        )}
      </select>
    </label>
  );
}

// Surfaces the stored value even if it's not in the canonical option list —
// so legacy / free-form server values pre-populate cleanly.
function SelectFreeField({ label, value, onChange, options }) {
  const merged = useMemo(() => {
    const set = new Set(options);
    if (value && !set.has(value)) set.add(value);
    return Array.from(set);
  }, [value, options]);
  return (
    <label className="block">
      {/* T142: same selective-clear affordance as GroupedSelectField. */}
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-text-secondary mb-1">
        <span>{label}</span>
        {value && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); onChange(""); }}
            className="text-rose-300 hover:text-rose-100 text-[10px] leading-none px-1 rounded hover:bg-rose-500/20"
            title={`Clear ${label}`}
          >×</button>
        )}
      </span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm"
      >
        <option value="">— not set —</option>
        {merged.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function TextField({ label, value, onChange, placeholder }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">{label}</span>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm"
      />
    </label>
  );
}

// Commentary editor — default mode shows the current text and a notice
// that the next save will auto-regenerate it from the edited ball data.
// Operator can opt in to manually overriding it (which then bypasses the
// auto-regenerate on save). Removing the override flips back to auto.
// Read-only commentary view with an "✨ Regenerate with AI" affordance.
// Manual override was removed per operator request — keeping a single
// AI-generated source of truth across the match. The regen button hits
// the same backend endpoint the Scorecard page uses (Claude Haiku,
// ~3 ZC per call). On success the parent (EditBallDialog) refreshes
// its local ball state so the new text shows immediately.
function CommentaryEditor({ ballId, currentText, onAfterRegen }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const regenerate = async () => {
    if (!ballId || busy) return;
    setBusy(true); setErr(""); setInfo("");
    try {
      const r = await import("../../api/axios").then((m) => m.default.post(
        `/api/scoring/balls/${ballId}/commentary/regenerate/`,
        {},
      ));
      const newText = r?.data?.commentary || "";
      const src = r?.data?.source || "claude";
      setInfo(src === "claude" ? "Regenerated (Claude)" : "Regenerated (template fallback)");
      onAfterRegen && onAfterRegen(newText);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.error || e?.message;
      setErr(detail || "Regen failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">
        Commentary
      </div>
      <div className="rounded border border-border-default bg-bg-secondary/60 p-3 text-sm text-text-primary whitespace-pre-wrap break-words mb-2 min-h-[3rem]">
        {currentText || <span className="text-text-secondary italic">— no commentary on file —</span>}
      </div>
      <div className="bg-bg-secondary/40 border border-border-default rounded p-3 flex items-start justify-between gap-3">
        <div className="text-xs text-text-secondary leading-relaxed">
          Commentary auto-regenerates from the ball's data when you save.
          Click below to also run it through Claude for a richer, vivid
          line. <span className="text-text-primary">~3 Zencredits per regen.</span>
        </div>
        <button
          type="button"
          onClick={regenerate}
          disabled={busy || !ballId}
          className="shrink-0 text-[11px] uppercase tracking-wider px-3 py-1.5 rounded bg-button-primary/20 border border-button-primary/40 text-button-primary hover:bg-button-primary/30 disabled:opacity-40 flex items-center gap-1"
        >
          {busy ? "Regenerating…" : "✨ Regenerate with AI"}
        </button>
      </div>
      {info && <div className="mt-2 text-[11px] text-emerald-300">{info}</div>}
      {err && <div className="mt-2 text-[11px] text-rose-300">{err}</div>}
    </div>
  );
}

function PlayerField({ label, value, onChange, players, subPlayers }) {
  const hasSubs = Array.isArray(subPlayers) && subPlayers.length > 0;
  // Match the wagon-wheel picker so optgroup labels read clearly on the
  // dark theme (browser defaults render as white text on white).
  const optgroupStyle = {
    background: "#1f2937",
    color: "#fef3c7",
    fontStyle: "normal",
    fontWeight: 700,
  };
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">{label}</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm"
      >
        <option value="">— select —</option>
        {hasSubs ? (
          <>
            <optgroup label="— Playing XI —" style={optgroupStyle}>
              {players.map((p) => <option key={p.id} value={p.id}>{playerDisplayName(p)}</option>)}
            </optgroup>
            <optgroup label="— Substitute fielders (squad reserves) —" style={optgroupStyle}>
              {subPlayers.map((p) => <option key={p.id} value={p.id}>{playerDisplayName(p)} (sub)</option>)}
            </optgroup>
          </>
        ) : (
          players.map((p) => <option key={p.id} value={p.id}>{playerDisplayName(p)}</option>)
        )}
      </select>
    </label>
  );
}

function SpatialEditor({ merged, setEdits, players, subFielders = [] }) {
  // 3-tab UX matches the live scoring screen.
  const [subTab, setSubTab] = useState("pitch");
  const [activeMatrix, setActiveMatrix] = useState("pitch");
  // Wagon-wheel needs local state so the picked region highlights visually.
  // Persists across re-renders within the dialog session; resets when the
  // dialog re-mounts (selecting a different ball).
  const [structuredRegion, setStructuredRegion] = useState(null);
  const sp = merged.spatial || {};
  const batterHand = merged.striker_hand || "RIGHT";
  // Patch via setEdits so the spread merge sees existing edits — same
  // pattern used by the parent's setSpatial(k,v) helper, just batched
  // for the matrix picker's onChange callback.
  const setSpatial = (patch) =>
    setEdits((s) => ({ ...s, spatial: { ...(s.spatial || {}), ...patch } }));
  // Wagon clicks also pre-fill fielders[0].fielding_position via SpatialPicker.
  // Persist that so the operator's pick survives Save.
  const setFielding = (fielding) =>
    setEdits((s) => ({ ...s, fielding }));
  // Single-field helper for the body-impact pickers below.
  const setOne = (k, v) =>
    setEdits((s) => ({ ...s, spatial: { ...(s.spatial || {}), [k]: v } }));
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1">
        {[
          { k: "pitch",  l: "Pitch map" },
          { k: "impact", l: "Height impact" },
          { k: "wagon",  l: "Wagon wheel" },
        ].map(({ k, l }) => (
          <button
            key={k}
            onClick={() => {
              setSubTab(k);
              if (k === "pitch")  setActiveMatrix("pitch");
              if (k === "impact") setActiveMatrix("height");
            }}
            className={`flex-1 text-[10px] px-2 py-1 rounded-md uppercase tracking-wider ${
              subTab === k
                ? "bg-button-primary text-white"
                : "bg-bg-secondary text-text-secondary hover:text-text-primary"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {(subTab === "pitch" || subTab === "impact") && (
        <ZoneMatrixPicker
          pitchZone={sp.pitch_zone ?? null}
          stumpZone={sp.stump_zone ?? null}
          heightZone={sp.height_zone ?? null}
          batterStumpZone={sp.batter_stump_zone ?? null}
          batterHand={batterHand}
          activeMatrix={activeMatrix}
          onActiveMatrixChange={setActiveMatrix}
          onChange={(pitchZone, stumpZone, heightZone, batterStumpZone) =>
            setSpatial({
              pitch_zone: pitchZone,
              stump_zone: stumpZone,
              height_zone: heightZone,
              batter_stump_zone: batterStumpZone,
            })
          }
        />
      )}

      {subTab === "wagon" && (
        <SpatialPicker
          ball={merged}
          setBall={(updater) => {
            const next = typeof updater === "function" ? updater(merged) : updater;
            // Persist both spatial and fielding updates — a wagon click
            // sets the spatial fields AND pre-fills fielders[0].fielding_position.
            // The earlier wrapper only forwarded spatial, so the auto-filled
            // fielding_position was lost on Save (root cause of the
            // "wagon wheel not working" report).
            if (next?.spatial) setSpatial(next.spatial);
            if (next?.fielding) setFielding(next.fielding);
          }}
          players={players}
          subFielders={subFielders}
          currentBowlerId={merged.bowler}
          wicketKeeperId={null}
          onRequestWicketKeeper={() => {}}
          batterHand={batterHand}
          structuredRegion={structuredRegion}
          onStructuredRegionChange={setStructuredRegion}
        />
      )}

      {/* Body impact — height + line where the ball hit the batter.
          Critical for LBW review and any "did it strike pad/body" call.
          Visible across all spatial sub-tabs since these fields aren't tied
          to the zone matrix; they describe what happened to the BATTER. */}
      <div className="border-t border-border-default pt-3 mt-1">
        <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-2">
          Body impact (where the ball hit the batter)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SelectFreeField
            label="Impact height"
            value={sp.batter_impact_height || ""}
            onChange={(v) => setOne("batter_impact_height", v)}
            options={BATTER_IMPACT_HEIGHTS}
          />
          <SelectFreeField
            label="Impact line (relative to stumps)"
            value={sp.batter_impact_line || ""}
            onChange={(v) => setOne("batter_impact_line", v)}
            options={BATTER_IMPACT_LINES}
          />
        </div>
      </div>
    </div>
  );
}

function BallPicker({ rows, selected, onChange }) {
  return (
    <div className="relative min-w-0">
      <select
        value={selected || ""}
        onChange={(e) => onChange(e.target.value)}
        className="bg-bg-secondary border border-border-default rounded px-2 py-1 pr-7 text-xs font-mono text-text-primary appearance-none truncate max-w-[300px]"
      >
        {rows.length === 0 && <option value="">No balls in this innings</option>}
        {rows.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
      </select>
      <ChevronDown className="absolute right-1 top-1.5 w-3.5 h-3.5 text-text-secondary pointer-events-none" />
    </div>
  );
}


// VideoClipEditor — full match recording + scrubber + clip-in/out
// pickers. Saves new offsets via POST /balls/<id>/reslice/ which both
// persists the offsets AND re-invokes the slicer so the per-ball clip
// MP4 in S3 is replaced. Once the Lambda finishes (sub-second for the
// -c copy path) the ball's clip-playback endpoint returns the new
// segment automatically.
function VideoClipEditor({ ball, onSavedClip }) {
  const videoRef = useRef(null);
  const [master, setMaster] = useState(null);   // {type, url, duration_ms, tier, ...}
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [clipIn,  setClipIn]  = useState(ball?.video_start_ms ?? null);
  const [clipOut, setClipOut] = useState(ball?.video_end_ms   ?? null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr("");
    getMatchMasterRecording(ball.match_id)
      .then((d) => { if (alive) setMaster(d); })
      .catch((e) => {
        if (!alive) return;
        const status = e?.response?.status;
        setErr(
          status === 404
            ? "No master recording exists for this match — record one before editing clips."
            : (e?.response?.data?.detail || "Couldn't load the master recording.")
        );
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ball.match_id]);

  // Keep clipIn / clipOut in sync if the parent reloads the ball.
  useEffect(() => {
    setClipIn(ball?.video_start_ms ?? null);
    setClipOut(ball?.video_end_ms ?? null);
  }, [ball?.id]);

  const seekTo = (ms) => {
    const v = videoRef.current;
    if (!v || ms == null) return;
    v.currentTime = ms / 1000;
  };
  const setInFromPlayhead = () => {
    const v = videoRef.current; if (!v) return;
    const ms = Math.round(v.currentTime * 1000);
    setClipIn(ms);
    // If clipOut is before the new clipIn, push it forward to avoid an
    // invalid range — operator can fine-tune afterwards.
    if (clipOut != null && clipOut <= ms) setClipOut(ms + 1000);
  };
  const setOutFromPlayhead = () => {
    const v = videoRef.current; if (!v) return;
    const ms = Math.round(v.currentTime * 1000);
    setClipOut(ms);
  };
  const nudge = (which, deltaMs) => {
    if (which === "in")  setClipIn((c)  => Math.max(0, (c ?? 0) + deltaMs));
    if (which === "out") setClipOut((c) => Math.max((clipIn ?? 0) + 1, (c ?? 0) + deltaMs));
  };

  const canSave = (
    clipIn != null && clipOut != null
    && clipOut > clipIn
    && (clipIn !== ball?.video_start_ms || clipOut !== ball?.video_end_ms)
  );

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true); setSavedMsg(""); setErr("");
    try {
      await resliceBall(ball.id, { video_start_ms: clipIn, video_end_ms: clipOut });
      setSavedMsg(`Saved · re-slicing now (clip refreshes in ~5 s)`);
      onSavedClip && onSavedClip(clipIn, clipOut);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Re-slice failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-text-secondary text-sm">Loading master recording…</div>;
  }
  if (err && !master) {
    return (
      <div className="bg-rose-500/10 border border-rose-500/30 rounded p-3 text-sm text-rose-300">
        {err}
      </div>
    );
  }
  if (master?.type === "ivs") {
    return (
      <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-sm text-amber-300">
        {master.detail}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <video
        ref={videoRef}
        src={master.url}
        controls
        preload="metadata"
        className="w-full max-h-[55vh] bg-black rounded"
        onLoadedMetadata={(e) => {
          // Jump to current clip-in on first load so the operator
          // immediately sees the moment the existing clip starts.
          if (clipIn != null) e.currentTarget.currentTime = clipIn / 1000;
        }}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <ClipBoundCard
          label="Clip-in"
          ms={clipIn}
          onSet={setInFromPlayhead}
          onSeek={() => seekTo(clipIn)}
          onNudge={(d) => nudge("in", d)}
        />
        <ClipBoundCard
          label="Clip-out"
          ms={clipOut}
          onSet={setOutFromPlayhead}
          onSeek={() => seekTo(clipOut)}
          onNudge={(d) => nudge("out", d)}
        />
      </div>

      <div className="bg-bg-secondary/60 border border-border-default rounded p-3 text-xs text-text-secondary leading-relaxed">
        <strong className="text-text-primary">How to re-clip:</strong>{" "}
        Play the master video, pause at the exact frame you want the
        ball's clip to begin, click <em>Set as clip-in</em>. Repeat at
        the end frame. Click <em>Save &amp; re-slice</em> — the per-ball
        clip on QC / scorecard refreshes within a few seconds.
      </div>

      {err && master && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded p-2 text-xs text-rose-300">{err}</div>
      )}
      {savedMsg && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2 text-xs text-emerald-300">{savedMsg}</div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-[11px] text-text-secondary">
          Master duration: {master?.duration_ms ? `${(master.duration_ms / 1000).toFixed(1)}s` : "—"}
          {clipIn != null && clipOut != null && (
            <span className="ml-3">
              New clip: {((clipOut - clipIn) / 1000).toFixed(2)}s
            </span>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={!canSave || saving}
          className="text-xs uppercase tracking-wider px-4 py-1.5 rounded bg-emerald-600 text-white disabled:opacity-40 flex items-center gap-1.5"
        >
          <Scissors className="w-3.5 h-3.5" />
          {saving ? "Saving…" : "Save & re-slice"}
        </button>
      </div>
    </div>
  );
}

function ClipBoundCard({ label, ms, onSet, onSeek, onNudge }) {
  const display = ms == null ? "—" : `${(ms / 1000).toFixed(2)}s (${ms} ms)`;
  return (
    <div className="bg-bg-secondary/40 border border-border-default rounded p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-text-secondary">{label}</span>
        <span className="font-mono text-xs text-text-primary">{display}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={onSet}
          className="text-[11px] px-2 py-1 rounded bg-button-primary/20 border border-button-primary/40 text-button-primary hover:bg-button-primary/30"
        >
          Set as {label.toLowerCase()}
        </button>
        <button
          onClick={onSeek}
          disabled={ms == null}
          className="text-[11px] px-2 py-1 rounded bg-bg-secondary border border-border-default text-text-secondary hover:text-text-primary disabled:opacity-40"
        >
          Jump
        </button>
        <button onClick={() => onNudge(-1000)} className="text-[11px] px-2 py-1 rounded bg-bg-secondary border border-border-default text-text-secondary hover:text-text-primary">−1s</button>
        <button onClick={() => onNudge(-100)}  className="text-[11px] px-2 py-1 rounded bg-bg-secondary border border-border-default text-text-secondary hover:text-text-primary">−100ms</button>
        <button onClick={() => onNudge(+100)}  className="text-[11px] px-2 py-1 rounded bg-bg-secondary border border-border-default text-text-secondary hover:text-text-primary">+100ms</button>
        <button onClick={() => onNudge(+1000)} className="text-[11px] px-2 py-1 rounded bg-bg-secondary border border-border-default text-text-secondary hover:text-text-primary">+1s</button>
      </div>
    </div>
  );
}

// Mutually-exclusive extras picker. Backend keeps four separate fields
// (wides/no_balls/byes/leg_byes) for legacy reasons but Law-21 / Law-23
// only ever allow ONE extras type per delivery. Old NumberField UI let
// the operator enter both `wides=1` and `no_balls=1` which created
// illegal balls that QC had to fix hours later. This picker enforces
// the rule at entry time: pick the type, then enter the runs for it.
function ExtrasPicker({ runs, onChange }) {
  const wides   = runs.wides    || 0;
  const noBalls = runs.no_balls || 0;
  const byes    = runs.byes     || 0;
  const legByes = runs.leg_byes || 0;
  const currentType =
    wides   > 0 ? "wide"    :
    noBalls > 0 ? "no_ball" :
    byes    > 0 ? "bye"     :
    legByes > 0 ? "leg_bye" :
    "legal";
  const currentRuns =
    currentType === "wide"    ? wides   :
    currentType === "no_ball" ? noBalls :
    currentType === "bye"     ? byes    :
    currentType === "leg_bye" ? legByes :
    0;
  const setExtras = (type, value) => {
    const v = Math.max(0, Number(value) || 0);
    onChange({
      wides:    type === "wide"    ? v : 0,
      no_balls: type === "no_ball" ? v : 0,
      byes:     type === "bye"     ? v : 0,
      leg_byes: type === "leg_bye" ? v : 0,
      // Reset no_ball_type when not a no-ball.
      ...(type !== "no_ball" ? { no_ball_type: "" } : {}),
    });
  };
  const options = [
    { v: "legal",   l: "Legal" },
    { v: "wide",    l: "Wide" },
    { v: "no_ball", l: "No-ball" },
    { v: "bye",     l: "Bye" },
    { v: "leg_bye", l: "Leg-bye" },
  ];
  return (
    <div className="col-span-full">
      <span className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1.5">
        Extras type · Law 21–23 (only one per delivery)
      </span>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {options.map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => setExtras(o.v, o.v === "legal" ? 0 : (currentRuns || 1))}
            className={`text-[11px] uppercase tracking-wider px-2.5 py-1.5 rounded-md border ${
              currentType === o.v
                ? "bg-amber-500/20 border-amber-400 text-amber-200"
                : "bg-bg-secondary border-border-default text-text-secondary hover:text-text-primary"
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>
      {currentType !== "legal" && (
        <label className="block">
          <span className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">
            {options.find((o) => o.v === currentType)?.l} runs
          </span>
          <input
            type="number"
            min={0}
            value={currentRuns}
            onChange={(e) => setExtras(currentType, e.target.value)}
            className="w-full bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm"
          />
        </label>
      )}
    </div>
  );
}
